// Minimal, renderer-agnostic polygon mesh - deliberately shaped like a Wavefront OBJ file: a flat
// list of vertex positions, a flat list of normals, and a list of faces referencing both (OBJ's
// "f v//vn" form). Faces may have any number of vertices (triangles, quads, n-gon caps); each
// renderer triangulates them however it needs to.
//
// Normals are stored, not derived from winding order, and that is the whole point. Both 3D views
// map this shared "ng" space (x = column axis, y/z = cross-section axes) into a render space that
// *mirrors* it - the SVG view flips x to point down, the three.js view swaps x and y for its Y-up
// world - and a mirror reverses handedness, so a normal recovered from the winding of a mirrored
// face points *inwards*. That silently inverted every solid: back-face culling kept the inside of
// each support and the shading was lit from the wrong side. Carrying explicit normals through the
// transform removes the failure mode entirely.
//
// Faces carry a single normal index each (flat shading) rather than one per vertex. Every solid
// built here is flat-faceted, so per-vertex normals would only ever repeat the face's own.
//
// Meshes are built once, as module-level constants (see supportMeshes.ts), in their own local
// model space and are then placed in the scene through a Transform (translation + yaw/pitch/roll
// rotation) - the same mesh can therefore be reused for every instance of a support, and neither
// 3D backend needs to know how the geometry was constructed.
import { dot, Vec3 } from "./extrusionEdges";

export interface Face {
  /** Indices into Mesh.positions, wound counter-clockwise seen from outside. */
  vertices: number[];
  /** Index into Mesh.normals - the face's outward normal, in the mesh's own model space. */
  normal: number;
  /**
   * Marks a face as one of many thin facets tessellating a genuinely curved surface (a
   * cylinder's round side, a sphere) rather than a real flat primitive face (a cube/prism/pyramid
   * face, or a cylinder's flat cap). See classifyMeshEdges: an edge between two smooth faces is a
   * tessellation seam, kept only when it is an exact (parameter-free) silhouette; an edge
   * touching any non-smooth face is a genuine corner of the solid and always kept.
   */
  smooth?: boolean;
  /**
   * How visibleMeshFaces computes this face's depth for painter's-algorithm ranking - see there.
   * Left undefined (centroid depth) for almost everything; "min" (the face's own furthest vertex)
   * is for a face wide enough, relative to its tilt towards the viewer, that its true depth varies
   * significantly across it and a plain centroid can rank it in front of unrelated nearby geometry
   * it does not actually overlap on screen - see the mounting plate behind a side support's
   * cylinder_pair rollers in supportMeshes.ts for the concrete case this exists for.
   */
  depthHint?: "min";
}

export interface Mesh {
  positions: Vec3[];
  normals: Vec3[];
  faces: Face[];
}

/** A mesh's positions and normals after being placed (and possibly scaled) into some space. */
interface PlacedMesh {
  positions: Vec3[];
  normals: Vec3[];
}

/**
 * Orientation as yaw/pitch/roll, in radians, applied roll -> pitch -> yaw.
 *
 * Model space uses the same axis convention as the rest of the viewport's 3D geometry: x is the
 * column axis ("up", positive upwards), y and z are the two cross-section axes. So yaw rotates
 * around the column axis (x), pitch around y and roll around z.
 */
export interface Rotation {
  yaw: number;
  pitch: number;
  roll: number;
}

interface Transform {
  position: Vec3;
  rotation: Rotation;
}

export const NO_ROTATION: Rotation = { yaw: 0, pitch: 0, roll: 0 };

export const degreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

type Axis = "x" | "y" | "z";

// The two remaining axes for each axis, ordered so that (axis, u, v) is a right-handed frame.
const AXIS_FRAME: Record<Axis, [Axis, Axis]> = {
  x: ["y", "z"],
  y: ["z", "x"],
  z: ["x", "y"],
};

/** Builds a Vec3 from a coordinate along `axis` plus the two in-plane coordinates (u, v). */
const fromAxisFrame = (axis: Axis, along: number, u: number, v: number): Vec3 => {
  const [uAxis, vAxis] = AXIS_FRAME[axis];
  const out: Vec3 = { x: 0, y: 0, z: 0 };
  out[axis] = along;
  out[uAxis] = u;
  out[vAxis] = v;
  return out;
};

const addVec = (a: Vec3, b: Vec3): Vec3 => ({ x: a.x + b.x, y: a.y + b.y, z: a.z + b.z });

const normalizeVec = (a: Vec3): Vec3 => {
  const length = Math.sqrt(dot(a, a));
  return length > 1e-12 ? { x: a.x / length, y: a.y / length, z: a.z / length } : { x: 0, y: 0, z: 0 };
};

const rotatePoint = (point: Vec3, rotation: Rotation): Vec3 => {
  const { yaw, pitch, roll } = rotation;

  // roll: around z
  const cosRoll = Math.cos(roll);
  const sinRoll = Math.sin(roll);
  const rolled: Vec3 = {
    x: point.x * cosRoll - point.y * sinRoll,
    y: point.x * sinRoll + point.y * cosRoll,
    z: point.z,
  };

  // pitch: around y
  const cosPitch = Math.cos(pitch);
  const sinPitch = Math.sin(pitch);
  const pitched: Vec3 = {
    x: rolled.x * cosPitch + rolled.z * sinPitch,
    y: rolled.y,
    z: -rolled.x * sinPitch + rolled.z * cosPitch,
  };

  // yaw: around x (the column axis)
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);
  return {
    x: pitched.x,
    y: pitched.y * cosYaw - pitched.z * sinYaw,
    z: pitched.y * sinYaw + pitched.z * cosYaw,
  };
};

/**
 * Places `mesh` by `transform`. A rotation is orthonormal, so normals rotate exactly like
 * positions - only the translation is skipped for them.
 */
export const transformMesh = (mesh: Mesh, transform: Transform): PlacedMesh => ({
  positions: mesh.positions.map((position) => addVec(rotatePoint(position, transform.rotation), transform.position)),
  normals: mesh.normals.map((normal) => rotatePoint(normal, transform.rotation)),
});

/**
 * Applies a non-uniform per-axis scale. Positions scale directly; normals must be scaled by the
 * *inverse* (the inverse transpose of a diagonal matrix), or a squashed face's normal would no
 * longer be perpendicular to it and front/back classification would be wrong wherever
 * scale3dX != scale3dYZ.
 */
export const scalePlacedMesh = (placed: PlacedMesh, scale: Vec3): PlacedMesh => ({
  positions: placed.positions.map((p) => ({ x: p.x * scale.x, y: p.y * scale.y, z: p.z * scale.z })),
  normals: placed.normals.map((n) => normalizeVec({ x: n.x / scale.x, y: n.y / scale.y, z: n.z / scale.z })),
});

/**
 * Uniformly scales a mesh. Normals are left untouched: a uniform positive scale does not change
 * any direction, so the inverse-transpose correction that scalePlacedMesh has to make for a
 * non-uniform scale is a no-op here.
 *
 * Used to turn the percentage-space meshes of supportMeshes.ts into world-space ones, once, at
 * module load - see SUPPORT_ICON_SIZE there.
 */
export const scaleMesh = (mesh: Mesh, factor: number): Mesh => ({
  positions: mesh.positions.map((p) => ({ x: p.x * factor, y: p.y * factor, z: p.z * factor })),
  normals: mesh.normals.map((normal) => ({ ...normal })),
  faces: mesh.faces.map((face) => ({
    vertices: [...face.vertices],
    normal: face.normal,
    smooth: face.smooth,
    depthHint: face.depthHint,
  })),
});

const faceCenter = (positions: Vec3[], face: Face): Vec3 => {
  const sum = face.vertices.reduce((total, index) => addVec(total, positions[index]), { x: 0, y: 0, z: 0 });
  const count = Math.max(1, face.vertices.length);
  return { x: sum.x / count, y: sum.y / count, z: sum.z / count };
};

/**
 * Newell's method - used by the builder to derive a normal from a freshly built polygon, where
 * the winding is known-good because it is authored in model space. Renderers must use the stored
 * normals instead, since their spaces mirror model space; see the note at the top.
 */
const windingNormal = (points: Vec3[]): Vec3 => {
  let x = 0;
  let y = 0;
  let z = 0;
  for (let i = 0; i < points.length; i++) {
    const current = points[i];
    const next = points[(i + 1) % points.length];
    x += (current.y - next.y) * (current.z + next.z);
    y += (current.z - next.z) * (current.x + next.x);
    z += (current.x - next.x) * (current.y + next.y);
  }
  return normalizeVec({ x, y, z });
};

/** Flat triangle-fan indices (0, i, i+1) for an n-gon - every face built here is convex. */
export const triangulateFan = (vertexCount: number): number[] => {
  const indices: number[] = [];
  for (let i = 1; i + 1 < vertexCount; i++) indices.push(0, i, i + 1);
  return indices;
};

interface VisibleFace {
  face: Face;
  /** The face's stored outward normal, placed into the same space as `positions`. */
  normal: Vec3;
  center: Vec3;
  /** Depth along the view direction - larger is closer to the camera. */
  depth: number;
}

/**
 * The front-facing faces of a mesh, given a placed mesh and a target-to-camera direction in that
 * same space, sorted furthest-away first so they can be painted back-to-front by a renderer
 * without a depth buffer.
 *
 * Both must be expressed in an *unmirrored* space (see the note at the top) - in practice the
 * shared ng space, scaled, with ISOMETRIC_VIEW_DIRECTION.
 */
export const visibleMeshFaces = (placed: PlacedMesh, mesh: Mesh, viewDirection: Vec3): VisibleFace[] =>
  mesh.faces
    .map((face) => {
      const normal = placed.normals[face.normal] ?? { x: 0, y: 0, z: 0 };
      const center = faceCenter(placed.positions, face);
      const depth =
        face.depthHint === "min"
          ? Math.min(...face.vertices.map((index) => dot(placed.positions[index], viewDirection)))
          : dot(center, viewDirection);
      return { face, normal, center, depth };
    })
    .filter((candidate) => dot(candidate.normal, viewDirection) > 0)
    .sort((a, b) => a.depth - b.depth);

/** Human-readable justification for a support mesh edge, mirroring selectExtrusionEdges' debugInfo
 * so "debug extrusion edges" can explain a support symbol's edges the same way it explains the
 * column's - see classifyMeshEdges below. */
interface MeshEdgeVisibility {
  from: number;
  to: number;
  /** Whether the edge is a genuine shape feature (silhouette or real corner) - see classifyMeshEdges. */
  keep: boolean;
  debugInfo: string;
}

/**
 * For every edge of `mesh` (deduplicated, same edge set edgesOfFaces would produce over *all*
 * faces), classifies it using the mesh's own construction knowledge instead of a tunable angle:
 * an edge between two "smooth" faces (see Face.smooth - the tessellation facets of a cylinder's
 * round side or a sphere) is a genuine feature only if it is an exact (parameter-free) silhouette
 * - its two adjacent faces disagree on front/back-facing - since there is no real corner between
 * two facets of the same curved surface, however finely it is tessellated (see
 * style.rollerSegments in supportMeshes.ts). Any edge touching a non-smooth face (a cube, prism
 * or pyramid face, or a cylinder's flat cap) is a real corner by construction - MeshBuilder only
 * ever puts an edge there because the two faces are genuinely, differently oriented - so it is
 * always kept, mirroring the both-flat case above.
 *
 * A cylinder cap's rim is one of these "always kept" edges (mixed: one smooth round-side facet,
 * one non-smooth flat cap). `keep` only says an edge is a real geometric feature; which parts of
 * it are hidden is left to the renderers' hidden-line handling, so the far rim of a cylinder ends
 * up half visible, half hidden - the classic technical-drawing cylinder.
 *
 * This mirrors selectExtrusionEdges' silhouette test exactly, without that function's separate
 * crease-angle threshold: there, the outline is arbitrary 2D input with no such "this vertex is a
 * deliberate corner" information to fall back on, so a real corner has to be told apart from a
 * tessellation seam by a threshold instead.
 *
 * A boundary edge (only one adjacent face - not expected for the closed solids built in
 * supportMeshes.ts, but handled defensively) is always kept, since there is no second face to
 * compare it against.
 *
 * `placed` must be expressed in the same (unmirrored, scaled) space as `viewDirection` - see the
 * note at the top of this file and visibleMeshFaces' docs.
 */
export const classifyMeshEdges = (mesh: Mesh, placed: PlacedMesh, viewDirection: Vec3): MeshEdgeVisibility[] => {
  const edgeKey = (a: number, b: number) => (a < b ? `${a}:${b}` : `${b}:${a}`);

  const facesByEdge = new Map<string, number[]>();
  mesh.faces.forEach((face, faceIndex) => {
    for (let i = 0; i < face.vertices.length; i++) {
      const from = face.vertices[i];
      const to = face.vertices[(i + 1) % face.vertices.length];
      const key = edgeKey(from, to);
      const faceIndices = facesByEdge.get(key) ?? [];
      faceIndices.push(faceIndex);
      facesByEdge.set(key, faceIndices);
    }
  });

  const viewDotOfFace = (faceIndex: number): number => {
    const normal = placed.normals[mesh.faces[faceIndex].normal] ?? { x: 0, y: 0, z: 0 };
    return dot(normal, viewDirection);
  };

  const results: MeshEdgeVisibility[] = [];
  const seen = new Set<string>();
  for (const face of mesh.faces) {
    for (let i = 0; i < face.vertices.length; i++) {
      const from = face.vertices[i];
      const to = face.vertices[(i + 1) % face.vertices.length];
      const key = edgeKey(from, to);
      if (seen.has(key)) continue;
      seen.add(key);

      const faceIndices = facesByEdge.get(key) ?? [];
      const viewDots = faceIndices.map(viewDotOfFace);

      if (faceIndices.length < 2) {
        results.push({
          from,
          to,
          keep: true,
          debugInfo: `edge: ${from} -> ${to}, faces: [${faceIndices.join(", ")}], keep: true (boundary edge)`,
        });
        continue;
      }

      const [faceA, faceB] = faceIndices;
      const [dotA, dotB] = viewDots;
      const isSilhouette = Math.sign(dotA) !== Math.sign(dotB);
      const bothSmooth = Boolean(mesh.faces[faceA].smooth) && Boolean(mesh.faces[faceB].smooth);
      const keep = isSilhouette || !bothSmooth;
      const debugInfo =
        `edge: ${from} -> ${to}, faces: [${faceA}, ${faceB}], viewDots: [${dotA.toFixed(3)}, ${dotB.toFixed(3)}], ` +
        `isSilhouette: ${isSilhouette}, bothSmooth: ${bothSmooth}, keep: ${keep} ` +
        `(${bothSmooth ? "tessellation seam, kept only if silhouette" : "real corner, always kept"})`;
      results.push({ from, to, keep, debugInfo });
    }
  }
  return results;
};

interface CubeArgs {
  /** Center of the box in model space. */
  center: Vec3;
  /** Full (not half) extent along each axis. */
  size: Vec3;
  /** Applied to all six faces - see Face.depthHint. */
  depthHint?: "min";
}

interface PrismArgs {
  /** Center of the prism, at mid-length along `axis`. */
  center: Vec3;
  /** Axis the profile is extruded along; the profile lies in the plane perpendicular to it. */
  axis: Axis;
  /**
   * The cross-section outline, as (u, v) pairs in that plane - see AXIS_FRAME for which two axes
   * u and v are. Convex, and closed implicitly (do not repeat the first point). Either winding is
   * accepted; a clockwise profile is reversed so the faces still come out facing outwards.
   */
  profile: [number, number][];
  /** Extent along `axis`. */
  length: number;
  /** Applied to every face - see Face.depthHint. */
  depthHint?: "min";
}

interface PyramidArgs {
  /** Apex of the pyramid, in model space. */
  apex: Vec3;
  /** Axis from the apex towards the base; the base outline lies in the plane perpendicular to it. */
  axis: Axis;
  /** Signed distance from the apex to the base plane, along `axis`. */
  height: number;
  /**
   * The base outline, as (u, v) pairs in that plane - see PrismArgs.profile, which this follows
   * exactly. Convex, closed implicitly. Either winding is accepted; a clockwise profile is
   * reversed so the faces still come out facing outwards.
   */
  profile: [number, number][];
}

interface CylinderArgs {
  /** Center of the cylinder (mid-length). */
  center: Vec3;
  /** Axis the cylinder is extruded along. */
  axis: Axis;
  radius: number;
  length: number;
  /** Number of segments around the circumference. */
  segments?: number;
}

interface SphereArgs {
  center: Vec3;
  radius: number;
  /** Segments around the polar axis (longitude). */
  segments?: number;
  /** Segments from pole to pole (latitude). */
  rings?: number;
  /** The pole-to-pole axis; defaults to the column axis. */
  axis?: Axis;
}

/**
 * Accumulates primitives into a single Mesh. Every helper appends to the same position/normal/face
 * lists, so one builder can produce a whole multi-part support symbol (plate + rollers + pyramid)
 * as one mesh with one consistent index space.
 *
 * Each helper supplies its faces' outward normals analytically (a cube's are the six axis
 * directions, a cylinder's side normals are radial, and so on) rather than leaving them to be
 * recovered from winding later.
 */
export class MeshBuilder {
  private positions: Vec3[] = [];
  private normals: Vec3[] = [];
  private faces: Face[] = [];
  private normalIndices = new Map<string, number>();

  /** Interns a normal, so the repeated axis normals of a multi-part solid are stored once. */
  private normalIndex(normal: Vec3): number {
    const unit = normalizeVec(normal);
    const key = `${unit.x.toFixed(6)}:${unit.y.toFixed(6)}:${unit.z.toFixed(6)}`;
    const existing = this.normalIndices.get(key);
    if (existing !== undefined) return existing;
    const index = this.normals.length;
    this.normals.push(unit);
    this.normalIndices.set(key, index);
    return index;
  }

  private addFace(vertices: number[], normal: Vec3, smooth = false, depthHint?: "min"): void {
    this.faces.push({ vertices, normal: this.normalIndex(normal), smooth: smooth || undefined, depthHint });
  }

  /** Appends `mesh`, optionally transformed, keeping face indices consistent. */
  addMesh(mesh: Mesh, transform?: Transform): this {
    const offset = this.positions.length;
    const placed = transform ? transformMesh(mesh, transform) : { positions: mesh.positions, normals: mesh.normals };
    this.positions.push(...placed.positions.map((position) => ({ ...position })));
    for (const face of mesh.faces) {
      this.addFace(
        face.vertices.map((index) => index + offset),
        placed.normals[face.normal] ?? { x: 0, y: 0, z: 0 },
        face.smooth,
        face.depthHint,
      );
    }
    return this;
  }

  addCube({ center, size, depthHint }: CubeArgs): this {
    const hx = size.x / 2;
    const hy = size.y / 2;
    const hz = size.z / 2;
    const offset = this.positions.length;

    for (const [sx, sy, sz] of [
      [-1, -1, -1],
      [1, -1, -1],
      [1, 1, -1],
      [-1, 1, -1],
      [-1, -1, 1],
      [1, -1, 1],
      [1, 1, 1],
      [-1, 1, 1],
    ] as const) {
      this.positions.push({ x: center.x + sx * hx, y: center.y + sy * hy, z: center.z + sz * hz });
    }

    const faces: [number[], Vec3][] = [
      [[0, 3, 2, 1], { x: 0, y: 0, z: -1 }],
      [[4, 5, 6, 7], { x: 0, y: 0, z: 1 }],
      [[0, 1, 5, 4], { x: 0, y: -1, z: 0 }],
      [[3, 7, 6, 2], { x: 0, y: 1, z: 0 }],
      [[0, 4, 7, 3], { x: -1, y: 0, z: 0 }],
      [[1, 2, 6, 5], { x: 1, y: 0, z: 0 }],
    ];
    for (const [vertices, normal] of faces) {
      this.addFace(
        vertices.map((index) => index + offset),
        normal,
        false,
        depthHint,
      );
    }
    return this;
  }

  /**
   * Extrudes a convex outline along `axis`: the general prism, of which the support symbols use
   * the triangular case (the same profile the 2D side views draw a pinned support as).
   */
  addPrism({ center, axis, profile, length, depthHint }: PrismArgs): this {
    if (profile.length < 3) return this;

    // Positive signed area means counter-clockwise seen from +axis, which is the winding the face
    // construction below assumes; flip a clockwise profile rather than emitting inward faces.
    const signedArea = profile.reduce((total, [u, v], index) => {
      const [nextU, nextV] = profile[(index + 1) % profile.length];
      return total + (u * nextV - nextU * v);
    }, 0);
    const outline = signedArea < 0 ? [...profile].reverse() : profile;

    const count = outline.length;
    const offset = this.positions.length;
    const half = length / 2;

    for (const along of [-half, half]) {
      for (const [u, v] of outline) this.positions.push(addVec(center, fromAxisFrame(axis, along, u, v)));
    }

    // side quads: the outward normal of edge (u1,v1)->(u2,v2) of a counter-clockwise outline is
    // (dv, -du), i.e. the edge direction turned right.
    for (let i = 0; i < count; i++) {
      const next = (i + 1) % count;
      const [u1, v1] = outline[i];
      const [u2, v2] = outline[next];
      this.addFace(
        [offset + i, offset + next, offset + count + next, offset + count + i],
        fromAxisFrame(axis, 0, v2 - v1, -(u2 - u1)),
        false,
        depthHint,
      );
    }

    const startCap: number[] = [];
    const endCap: number[] = [];
    for (let i = 0; i < count; i++) {
      startCap.push(offset + (count - 1 - i));
      endCap.push(offset + count + i);
    }
    this.addFace(startCap, fromAxisFrame(axis, -1, 0, 0), false, depthHint);
    this.addFace(endCap, fromAxisFrame(axis, 1, 0, 0), false, depthHint);
    return this;
  }

  /**
   * Tapers a convex outline down to a point: the general pyramid, of which the support symbols
   * use the square-based case (the "pinned in both directions" solid in the support sketch, where
   * two crossed triangular prisms would double up the same corners instead of meeting at one
   * apex).
   *
   * Unlike a prism's side quads, a side face here is not parallel to `axis`, so its normal is not
   * the simple "edge direction turned right" - it needs an along-axis component too. Both come out
   * of one cross product, (corner_i - apex) x (corner_i+1 - apex), computed directly in the local
   * (along, u, v) numbers rather than in xyz: AXIS_FRAME is a cyclic relabelling of (x, y, z) for
   * every axis, i.e. a rotation, so a vector built from those local components and passed through
   * fromAxisFrame is exactly the same vector a cross product of the real 3D edges would give.
   */
  addPyramid({ apex, axis, height, profile }: PyramidArgs): this {
    if (profile.length < 3) return this;

    // Same convention as addPrism: the outline must be counter-clockwise seen from +axis.
    const signedArea = profile.reduce((total, [u, v], index) => {
      const [nextU, nextV] = profile[(index + 1) % profile.length];
      return total + (u * nextV - nextU * v);
    }, 0);
    const outline = signedArea < 0 ? [...profile].reverse() : profile;

    const count = outline.length;
    const apexIndex = this.positions.length;
    this.positions.push({ ...apex });
    const baseOffset = this.positions.length;
    for (const [u, v] of outline) this.positions.push(addVec(apex, fromAxisFrame(axis, height, u, v)));

    for (let i = 0; i < count; i++) {
      const next = (i + 1) % count;
      const [u1, v1] = outline[i];
      const [u2, v2] = outline[next];
      // cross((height, u1, v1), (height, u2, v2)) in the local (along, u, v) frame.
      const normal = fromAxisFrame(axis, u1 * v2 - v1 * u2, height * (v1 - v2), height * (u2 - u1));
      this.addFace([apexIndex, baseOffset + i, baseOffset + next], normal);
    }

    // The base is CCW from +axis (same as a prism's end cap); its outward normal faces away from
    // the apex, i.e. further along the direction from apex to base, so it flips with the sign of
    // `height` exactly like a prism's start/end caps flip between -axis and +axis.
    const baseCap = Array.from({ length: count }, (_, i) => baseOffset + i);
    this.addFace(height < 0 ? baseCap.reverse() : baseCap, fromAxisFrame(axis, height < 0 ? -1 : 1, 0, 0));
    return this;
  }

  addCylinder({ center, axis, radius, length, segments = 16 }: CylinderArgs): this {
    const count = Math.max(3, Math.round(segments));
    const offset = this.positions.length;
    const half = length / 2;

    for (const along of [-half, half]) {
      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI * i) / count;
        this.positions.push(
          addVec(center, fromAxisFrame(axis, along, radius * Math.cos(angle), radius * Math.sin(angle))),
        );
      }
    }

    // side quads: the outward normal is radial, taken at the middle of the segment's arc. Marked
    // smooth - these are tessellation facets of the round side surface, not real corners (see
    // Face.smooth/classifyMeshEdges).
    for (let i = 0; i < count; i++) {
      const next = (i + 1) % count;
      const angle = (2 * Math.PI * (i + 0.5)) / count;
      this.addFace(
        [offset + i, offset + next, offset + count + next, offset + count + i],
        fromAxisFrame(axis, 0, Math.cos(angle), Math.sin(angle)),
        true,
      );
    }

    const bottomCap: number[] = [];
    const topCap: number[] = [];
    for (let i = 0; i < count; i++) {
      bottomCap.push(offset + (count - 1 - i));
      topCap.push(offset + count + i);
    }
    this.addFace(bottomCap, fromAxisFrame(axis, -1, 0, 0));
    this.addFace(topCap, fromAxisFrame(axis, 1, 0, 0));
    return this;
  }

  /**
   * A UV sphere: quads between the latitude rings, triangle fans at the two poles. Every face is
   * flat-shaded from its own plane normal, so it reads as a faceted ball - raise `segments` and
   * `rings` for a rounder one, at the cost of that many more faces and outline edges.
   */
  addSphere({ center, radius, segments = 16, rings = 8, axis = "x" }: SphereArgs): this {
    const around = Math.max(3, Math.round(segments));
    const bands = Math.max(2, Math.round(rings));

    // A UV sphere's faces are planar (the pole fans trivially, the ring quads by symmetry about
    // the plane bisecting their two longitudes), so the polygon's own plane normal is exact - and
    // it is what a flat-shaded face must be lit and culled by. The radial direction through the
    // face center is close but not equal to it, and differs most at the poles.
    const planeNormal = (vertices: number[]): Vec3 => windingNormal(vertices.map((index) => this.positions[index]));

    const northPole = this.positions.length;
    this.positions.push(addVec(center, fromAxisFrame(axis, radius, 0, 0)));

    // one row of `around` vertices per interior latitude; the poles replace the two outer rows
    const rowStart = this.positions.length;
    for (let band = 1; band < bands; band++) {
      const polar = (Math.PI * band) / bands;
      const along = radius * Math.cos(polar);
      const ringRadius = radius * Math.sin(polar);
      for (let i = 0; i < around; i++) {
        const angle = (2 * Math.PI * i) / around;
        this.positions.push(
          addVec(center, fromAxisFrame(axis, along, ringRadius * Math.cos(angle), ringRadius * Math.sin(angle))),
        );
      }
    }

    const southPole = this.positions.length;
    this.positions.push(addVec(center, fromAxisFrame(axis, -radius, 0, 0)));

    const ringVertex = (band: number, i: number) => rowStart + (band - 1) * around + (i % around);

    for (let i = 0; i < around; i++) {
      const fan = [northPole, ringVertex(1, i), ringVertex(1, i + 1)];
      this.addFace(fan, planeNormal(fan), true);
    }

    for (let band = 1; band < bands - 1; band++) {
      for (let i = 0; i < around; i++) {
        const quad = [
          ringVertex(band + 1, i),
          ringVertex(band + 1, i + 1),
          ringVertex(band, i + 1),
          ringVertex(band, i),
        ];
        this.addFace(quad, planeNormal(quad), true);
      }
    }

    for (let i = 0; i < around; i++) {
      const fan = [southPole, ringVertex(bands - 1, i + 1), ringVertex(bands - 1, i)];
      this.addFace(fan, planeNormal(fan), true);
    }
    return this;
  }

  build(): Mesh {
    return { positions: this.positions, normals: this.normals, faces: this.faces };
  }
}
